import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import DisableConfirm from "@/components/DisableConfirm";
import { toast } from "sonner";
import { Eye, Pencil, Power, Plus, RotateCcw, Search } from "lucide-react";

const FREQ_COLORS = {
  Daily: "bg-rose-50 text-rose-700 border-rose-200",
  Weekly: "bg-orange-50 text-orange-700 border-orange-200",
  Fortnightly: "bg-amber-50 text-amber-700 border-amber-200",
  Monthly: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Bi-Monthly": "bg-teal-50 text-teal-700 border-teal-200",
  Quarterly: "bg-sky-50 text-sky-700 border-sky-200",
  "Half-Yearly": "bg-indigo-50 text-indigo-700 border-indigo-200",
  Yearly: "bg-violet-50 text-violet-700 border-violet-200",
};

export default function PeriodicalsList() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [pubs, setPubs] = useState([]);
  const [orgFilter, setOrgFilter] = useState("all");
  const [pubFilter, setPubFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [view, setView] = useState(null);
  const [disableTarget, setDisableTarget] = useState(null);
  const [disabling, setDisabling] = useState(false);

  const load = async (p = page) => {
    setData(null);
    try {
      const params = { page: p, page_size: 20 };
      if (search) params.search = search;
      if (pubFilter && pubFilter !== "all") params.publication_house_id = pubFilter;
      else if (orgFilter && orgFilter !== "all") params.organization_id = orgFilter;
      const { data: res } = await api.get("/periodicals", { params });
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setData({ items: [], total: 0, page: 1, page_size: 20 });
    }
  };

  useEffect(() => { (async () => { try { const { data } = await api.get("/organizations/active-list"); setOrgs(data); } catch { /* */ } })(); }, []);
  useEffect(() => {
    setPubFilter("all");
    if (!orgFilter || orgFilter === "all") { setPubs([]); return; }
    (async () => { try { const { data } = await api.get("/publication-houses/active-list", { params: { organization_id: orgFilter } }); setPubs(data); } catch { /* */ } })();
  }, [orgFilter]);
  useEffect(() => { load(1); setPage(1); /* eslint-disable-next-line */ }, [orgFilter, pubFilter]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const handleDisable = async () => {
    setDisabling(true);
    try {
      await api.patch(`/periodicals/${disableTarget.id}/disable`);
      toast.success("Periodical disabled");
      setDisableTarget(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setDisabling(false); }
  };

  const handleEnable = async (item) => {
    try {
      await api.patch(`/periodicals/${item.id}/enable`);
      toast.success("Periodical re-enabled"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div data-testid="periodicals-list-page" className="mx-auto max-w-7xl">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Module 03</div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">Periodicals</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">RNI/PRGI registered periodicals. Cascading filters from Organization → Publication House.</p>
        </div>
        <Button asChild data-testid="periodical-create-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#00227A]">
          <Link to="/periodicals/new"><Plus size={16} className="mr-1.5" />Create Periodical</Link>
        </Button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <form onSubmit={(e) => { e.preventDefault(); load(1); setPage(1); }} className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="per-search-input" placeholder="Search by title…" value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-sm pl-9" />
        </form>
        <Select value={orgFilter} onValueChange={setOrgFilter}>
          <SelectTrigger data-testid="per-org-filter" className="w-[220px] rounded-sm"><SelectValue placeholder="Organization" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Organizations</SelectItem>
            {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={pubFilter} onValueChange={setPubFilter} disabled={!orgFilter || orgFilter === "all"}>
          <SelectTrigger data-testid="per-pub-filter" className="w-[220px] rounded-sm"><SelectValue placeholder="Publication House" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Pub Houses</SelectItem>
            {pubs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-sm border border-slate-200 bg-white">
        <Table data-testid="per-table" className="data-table">
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>RNI/PRGI</TableHead>
              <TableHead>Frequency</TableHead>
              <TableHead>Pub House</TableHead>
              <TableHead>Language</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data === null && [...Array(5)].map((_, i) => (
              <TableRow key={i}><TableCell colSpan={7}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
            ))}
            {data && data.items.length === 0 && (
              <TableRow><TableCell colSpan={7} className="py-12 text-center text-sm text-slate-500">No periodicals found.</TableCell></TableRow>
            )}
            {data?.items.map((it) => (
              <TableRow key={it.id} data-testid={`per-row-${it.id}`}>
                <TableCell><div className="font-medium text-slate-900">{it.title}</div><div className="text-xs text-slate-500 font-mono">{it.issn_number ? `ISSN ${it.issn_number}` : "—"}</div></TableCell>
                <TableCell className="font-mono text-xs">{it.registration_number || "—"}</TableCell>
                <TableCell><span className={`rounded-sm border px-2 py-0.5 text-xs font-medium ${FREQ_COLORS[it.frequency] || "border-slate-200 bg-slate-50 text-slate-700"}`}>{it.frequency}</span></TableCell>
                <TableCell className="text-sm text-slate-700">{it.publication_house_name || "—"}</TableCell>
                <TableCell className="text-sm text-slate-700">{it.language}</TableCell>
                <TableCell><StatusBadge status={it.status} /></TableCell>
                <TableCell className="text-right whitespace-nowrap" style={{ minWidth: 160 }}>
                  <div className="flex items-center justify-end gap-1">
                    <Button size="icon" variant="ghost" title="View details" aria-label="View" onClick={() => setView(it)} data-testid={`per-view-${it.id}`} className="h-8 w-8 rounded-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900"><Eye size={16} /></Button>
                    <Button size="icon" variant="ghost" title="Edit" aria-label="Edit" onClick={() => navigate(`/periodicals/${it.id}/edit`)} data-testid={`per-edit-${it.id}`} className="h-8 w-8 rounded-sm text-[#002FA7] hover:bg-indigo-50 hover:text-[#00227A]"><Pencil size={16} /></Button>
                    {it.status !== "disabled" ? (
                      <Button size="icon" variant="ghost" title="Disable (soft delete)" aria-label="Disable" onClick={() => setDisableTarget(it)} data-testid={`per-disable-${it.id}`} className="h-8 w-8 rounded-sm text-rose-600 hover:bg-rose-50 hover:text-rose-700"><Power size={16} /></Button>
                    ) : (
                      <Button size="icon" variant="ghost" title="Re-enable" aria-label="Re-enable" onClick={() => handleEnable(it)} data-testid={`per-enable-${it.id}`} className="h-8 w-8 rounded-sm text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"><RotateCcw size={16} /></Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data && data.total > 0 && (
          <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 text-sm text-slate-600">
            <div>Page <span className="font-medium">{page}</span> of {totalPages} — {data.total} total</div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => { setPage(page - 1); load(page - 1); }} className="rounded-sm" data-testid="per-prev">Prev</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => { setPage(page + 1); load(page + 1); }} className="rounded-sm" data-testid="per-next">Next</Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent data-testid="per-view-modal" className="max-w-2xl rounded-sm">
          <DialogHeader>
            <DialogTitle>{view?.title}</DialogTitle>
            <DialogDescription>Periodical details</DialogDescription>
          </DialogHeader>
          {view && (
            <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <Info label="Pub House" value={view.publication_house_name} />
              <Info label="Status" value={<StatusBadge status={view.status} />} />
              <Info label="Title Code" value={view.title_code} mono />
              <Info label="RNI/PRGI" value={view.registration_number} mono />
              <Info label="Reg Date" value={view.registration_date} />
              <Info label="ISSN" value={view.issn_number} mono />
              <Info label="Frequency" value={view.frequency} />
              <Info label="Language" value={view.language} />
              <Info label="Category" value={view.category} />
              <Info label="Editor" value={view.editor_name} />
              <Info label="Place" value={view.place_of_publication} />
              <Info label="Entry IP" value={view.entry_ip} mono />
              <div className="sm:col-span-2"><Info label="Printing Press" value={view.printing_press_details} /></div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <DisableConfirm
        open={!!disableTarget}
        onOpenChange={(o) => !o && setDisableTarget(null)}
        onConfirm={handleDisable}
        loading={disabling}
        entityLabel="periodical"
        warningMessage="Disabling this periodical will stop all subscription and billing processes associated with it. This action is logged for audit purposes."
      />
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div className="rounded-sm border border-slate-200 p-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1 text-sm text-slate-900 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}
